import { DomainDto } from '../../../../dtos/domain'
import { NotificationAutoSuggestFields } from '../../../../enums/notification-auto-suggest-fields.enum'
import { INotificationFieldSuggestions } from '../../../../interfaces/notification-field-suggestions'
import { extract } from '../../../../_utils/object'
import { IEnhancedNotificationFieldSuggestions, SuggestedFieldsForm } from './types'

/**
 * Determines if a field can be shown based on domain configuration
 */
export function canShowField(domain: DomainDto, field: keyof INotificationFieldSuggestions): boolean {
    const allowedFields = domain.notificationAutoSuggestFields
    const key = (field === 'imageUrl' ? 'image_url' : field) as NotificationAutoSuggestFields
    return allowedFields === null ? true : allowedFields?.includes(key) ?? false
}

/**
 * Sorts keywords alphabetically for display
 */
export function sortKeywordsForDisplay(keywords: string[] | null): string[] {
    if (!keywords || !keywords.length) return []

    // First, sort alphabetically
    const sorted = [...keywords].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))

    // For flexbox row-major layout to appear column-major, we need to reorder items
    // If we have items [A,B,C,D,E,F] and want 3 columns:
    // Column layout: A D    Row layout needed: A B C
    //                B E                       D E F
    //                C F
    // So we need to arrange as: [A,B,C,D,E,F] -> [A,D,B,E,C,F]

    const columns = 3
    const rows = Math.ceil(sorted.length / columns)
    const reordered: string[] = []

    // Fill by rows, but pick items from column positions
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < columns; col++) {
            const index = col * rows + row
            if (index < sorted.length) {
                reordered.push(sorted[index])
            }
        }
    }

    return reordered
}

/**
 * Extracts form selections for page analyzer results
 */
export function extractFormSelections(
    values: SuggestedFieldsForm,
    variant: IEnhancedNotificationFieldSuggestions,
    domain: DomainDto,
    selectedKeywords?: string[] | null,
): Partial<INotificationFieldSuggestions> {
    let selections: Partial<INotificationFieldSuggestions> = {}

    if (values.title && canShowField(domain, 'title')) {
        selections.title = variant.title
    }
    if (values.body && canShowField(domain, 'body')) {
        selections.body = variant.body
    }
    if (values.imageUrl && canShowField(domain, 'imageUrl')) {
        if (variant.compositeImage) {
            selections.compositeImage = variant.compositeImage
            selections.imageUrl = variant.imageUrl
        } else {
            const suggestedImage = variant.compositeImage ? extract(variant.compositeImage, 'url') : variant.imageUrl
            selections.imageUrl = suggestedImage
        }
    }
    if ((values.keywords || selectedKeywords?.length) && canShowField(domain, 'keywords')) {
        const suggestedKws = variant ? extract(variant, 'keywords', []) : []
        selections.keywords = values.keywords ? suggestedKws : selectedKeywords
    }

    return selections
}

/**
 * Builds all available selections for AI variants (used for adding complete variants)
 */
export function buildAllSelections(
    variant: IEnhancedNotificationFieldSuggestions,
    domain: DomainDto,
): Partial<INotificationFieldSuggestions> {
    let selections: Partial<INotificationFieldSuggestions> = {}

    const cardSuggestedImage = variant?.compositeImage ? extract(variant.compositeImage, 'url') : variant?.imageUrl

    const cardSuggestedKws = variant ? extract(variant, 'keywords', []) : []

    const hasTitleSuggestion = !!variant?.title
    const hasBodySuggestion = !!variant?.body
    const hasImageSuggestion = cardSuggestedImage !== null
    const hasKwSuggestions = cardSuggestedKws.length > 0

    // Always include title if available (required for variants)
    if (hasTitleSuggestion && canShowField(domain, 'title')) {
        selections.title = variant.title
    }
    // Always include body if available
    if (hasBodySuggestion && canShowField(domain, 'body')) {
        selections.body = variant.body
    }
    // Include image if available
    if (hasImageSuggestion && canShowField(domain, 'imageUrl')) {
        if (variant.compositeImage) {
            selections.compositeImage = variant.compositeImage
            selections.imageUrl = variant.imageUrl
        } else {
            selections.imageUrl = cardSuggestedImage
        }
    }
    // Include keywords if available
    if (hasKwSuggestions && canShowField(domain, 'keywords')) {
        selections.keywords = cardSuggestedKws
    }

    return selections
}
